import type { Database } from '@planmax/shared-types'
import type { BadgeProps } from '@/components/ui/badge'
import type { ColumnGroupConfig, SavedViewTabConfig } from '@/components/data-table'

// ============================================
// Enums from Database
// ============================================

export type TransientStatus = Database['public']['Enums']['transient_status']
export type BookingType = Database['public']['Enums']['booking_type']
export type BookingSource = Database['public']['Enums']['booking_source']
export type SizeCategory = Database['public']['Enums']['size_category']
export type RateType = Database['public']['Enums']['rate_type']

// ============================================
// Enum Arrays for Iteration
// ============================================

export const TRANSIENT_STATUSES = [
  'inquiry',
  'reserved',
  'confirmed',
  'checked_in',
  'checked_out',
  'cancelled',
  'no_show',
] as const

export const BOOKING_TYPES = ['guaranteed', 'flex'] as const

export const BOOKING_SOURCES = [
  'phone',
  'email',
  'customer_portal',
  'walk_in',
  'quick_fit',
] as const

export const SIZE_CATEGORIES = ['small', 'medium', 'large', 'xlarge'] as const

export const RATE_TYPES = ['flat', 'per_sqft', 'per_night'] as const

// ============================================
// Enum Labels
// ============================================

export const TRANSIENT_STATUS_LABELS: Record<TransientStatus, string> = {
  inquiry: 'Inquiry',
  reserved: 'Reserved',
  confirmed: 'Confirmed',
  checked_in: 'Checked In',
  checked_out: 'Checked Out',
  cancelled: 'Cancelled',
  no_show: 'No Show',
}

export const BOOKING_TYPE_LABELS: Record<BookingType, string> = {
  guaranteed: 'Guaranteed',
  flex: 'Flex',
}

export const BOOKING_TYPE_FILTER_OPTIONS = BOOKING_TYPES.map((type) => ({
  value: type,
  label: BOOKING_TYPE_LABELS[type],
}))

export const BOOKING_SOURCE_LABELS: Record<BookingSource, string> = {
  phone: 'Phone',
  email: 'Email',
  flightbridge: 'Integration',
  customer_portal: 'Customer Portal',
  walk_in: 'Walk-In',
  quick_fit: 'Quick Fit',
}

export const SIZE_CATEGORY_LABELS: Record<SizeCategory, string> = {
  small: 'Small',
  medium: 'Medium',
  large: 'Large',
  xlarge: 'X-Large',
}

export const RATE_TYPE_LABELS: Record<RateType, string> = {
  flat: 'Flat Rate',
  per_sqft: 'Per Sq Ft',
  per_night: 'Per Night',
}

// ============================================
// Space Type (derived from unit_type)
// ============================================

export type SpaceType = 'hangar' | 'tiedown' | 'ramp'

export const SPACE_TYPE_LABELS: Record<SpaceType, string> = {
  hangar: 'Hangar',
  tiedown: 'Tiedown',
  ramp: 'Ramp',
}

export const SPACE_TYPES = ['hangar', 'tiedown', 'ramp'] as const

export const SPACE_TYPE_FILTER_OPTIONS = SPACE_TYPES.map((type) => ({
  value: type,
  label: SPACE_TYPE_LABELS[type],
}))

/** Map DB unit_type enum to simplified SpaceType */
export function deriveSpaceType(unitType: string | null): SpaceType | null {
  if (!unitType) return null
  switch (unitType) {
    case 'conventional_hangar':
    case 'box_hangar':
    case 't_hangar':
    case 'shade':
      return 'hangar'
    case 'tiedown':
      return 'tiedown'
    case 'ramp':
      return 'ramp'
    default:
      return null
  }
}

// ============================================
// Invoice Status (derived from stay data)
// ============================================

export type InvoiceStatus = 'invoiced' | 'uninvoiced' | 'overdue'

export const INVOICE_STATUS_LABELS: Record<InvoiceStatus, string> = {
  invoiced: 'Invoiced',
  uninvoiced: 'Uninvoiced',
  overdue: 'Overdue',
}

export const INVOICE_STATUS_VARIANTS: Record<InvoiceStatus, BadgeVariant> = {
  invoiced: 'default',
  uninvoiced: 'secondary',
  overdue: 'destructive',
}

const OVERDUE_THRESHOLD_DAYS = 7

/** Derive invoice status from stay data */
export function getInvoiceStatus(
  stay: Pick<TransientStayTableRow, 'status' | 'invoice_id' | 'actual_check_out'>
): InvoiceStatus | null {
  if (stay.status !== 'checked_out') return null
  if (stay.invoice_id) return 'invoiced'
  if (stay.actual_check_out) {
    const checkOutDate = new Date(stay.actual_check_out)
    const daysSince = (Date.now() - checkOutDate.getTime()) / (1000 * 60 * 60 * 24)
    if (daysSince > OVERDUE_THRESHOLD_DAYS) return 'overdue'
  }
  return 'uninvoiced'
}

// ============================================
// Badge Variants for Status
// ============================================

type BadgeVariant = BadgeProps['variant']

export const TRANSIENT_STATUS_VARIANTS: Record<TransientStatus, BadgeVariant> = {
  inquiry: 'secondary',
  reserved: 'secondary',
  confirmed: 'default',
  checked_in: 'default',
  checked_out: 'outline',
  cancelled: 'destructive',
  no_show: 'destructive',
}

export const BOOKING_TYPE_VARIANTS: Record<BookingType, BadgeVariant> = {
  guaranteed: 'default',
  flex: 'secondary',
}

// ============================================
// Table Row Type (with joined data)
// ============================================

export interface TransientStayTableRow {
  // Core
  id: string
  organization_id: string
  location_id: string
  booking_number: string

  // Parties (with joined names)
  client_id: string
  client_name: string | null
  aircraft_id: string
  tail_number: string | null
  aircraft_type_id: string | null
  aircraft_type_name: string | null

  // Location (with joined data)
  unit_id: string | null
  unit_code: string | null
  unit_type: string | null
  building_id: string | null
  building_name: string | null
  location_name: string | null

  // When
  check_in: string
  expected_check_out: string
  actual_check_out: string | null
  nights_count: number | null

  // Space Requirements
  sqft_required: number | null
  wingspan: number | null
  length: number | null
  size_category: SizeCategory | null

  // Booking details
  booking_type: BookingType
  source: BookingSource | null
  status: TransientStatus

  // Pricing
  rate_type: RateType | null
  rate_amount: number | null
  subtotal: number | null
  discount_amount: number | null
  discount_reason: string | null
  tax_amount: number | null
  total_amount: number | null

  // Billing
  invoice_id: string | null
  invoiced_at: string | null

  // Allocation
  allocation_id: string | null

  // Notes
  notes: string | null
  special_requests: string | null

  // Cancellation
  cancellation_reason: string | null

  // Audit
  created_at: string
  updated_at: string | null
}

// ============================================
// Form Data Type
// ============================================

export interface TransientStayFormData {
  // Required fields
  location_id: string
  client_id: string
  aircraft_id: string
  unit_id: string | null
  check_in: string
  expected_check_out: string
  booking_type: BookingType
  status: TransientStatus

  // Optional fields
  aircraft_type_id?: string | null
  actual_check_out?: string | null

  // Space requirements (auto-filled from aircraft)
  sqft_required?: number | null
  wingspan?: number | null
  length?: number | null
  size_category?: SizeCategory | null

  // Booking source
  source?: BookingSource | null

  // Pricing
  rate_type?: RateType | null
  rate_amount?: number | null
  discount_amount?: number | null
  discount_reason?: string | null
  tax_amount?: number | null

  // Notes
  notes?: string | null
  special_requests?: string | null

  // Allocation link
  allocation_id?: string | null
}

// ============================================
// Filter Type
// ============================================

export interface TransientStayFilters {
  search?: string
  status?: TransientStatus[]
  bookingType?: BookingType[]
  locationId?: string
  dateFrom?: string
  dateTo?: string
}

// ============================================
// Page Tab Definitions (Time-Oriented)
// ============================================

export type TransientTimeTab = 'today' | 'upcoming' | 'checked_out' | 'all'

export interface TransientTimeTabConfig {
  value: TransientTimeTab
  label: string
  description?: string
}

export const TRANSIENT_TIME_TABS: TransientTimeTabConfig[] = [
  { value: 'today', label: 'Today' },
  { value: 'upcoming', label: 'Upcoming', description: 'Next 14 days' },
  { value: 'checked_out', label: 'Checked Out', description: 'Last 30 days' },
  { value: 'all', label: 'All' },
]

/** SavedViewTabs-compatible config (uses `id` instead of `value`) */
export const TRANSIENT_SAVED_VIEWS: SavedViewTabConfig<TransientTimeTab>[] = [
  { id: 'today', label: 'Today' },
  { id: 'upcoming', label: 'Upcoming', description: 'Next 14 days' },
  { id: 'checked_out', label: 'Checked Out', description: 'Last 30 days' },
  { id: 'all', label: 'All' },
]

// ============================================
// Today View Sections
// ============================================

export interface TodaySections {
  arriving: TransientStayTableRow[]
  onGround: TransientStayTableRow[]
  departing: TransientStayTableRow[]
}

/**
 * Categorize transients into Today view sections
 * - Arriving: status in ['reserved', 'confirmed'] AND check_in date = selectedDate
 * - On Ground: status = 'checked_in'
 * - Departing: status = 'checked_in' AND expected_check_out date = selectedDate
 */
export function categorizeTodayTransients(
  transients: TransientStayTableRow[],
  selectedDate: Date
): TodaySections {
  const dateStr = selectedDate.toISOString().split('T')[0]

  const arriving: TransientStayTableRow[] = []
  const onGround: TransientStayTableRow[] = []
  const departing: TransientStayTableRow[] = []

  for (const transient of transients) {
    const checkInDate = transient.check_in.split('T')[0]
    const checkOutDate = transient.expected_check_out.split('T')[0]

    // On Ground: Currently checked in
    if (transient.status === 'checked_in') {
      onGround.push(transient)

      // Also add to departing if checkout is today
      if (checkOutDate === dateStr) {
        departing.push(transient)
      }
    }
    // Arriving: Reserved or confirmed with check-in today
    else if (
      (transient.status === 'reserved' || transient.status === 'confirmed') &&
      checkInDate === dateStr
    ) {
      arriving.push(transient)
    }
  }

  return { arriving, onGround, departing }
}

/**
 * Get upcoming transients (next N days)
 */
export function getUpcomingTransients(
  transients: TransientStayTableRow[],
  days: number = 14
): TransientStayTableRow[] {
  const now = new Date()
  const endDate = new Date(now)
  endDate.setDate(endDate.getDate() + days)

  return transients.filter((t) => {
    if (!isUpcoming(t.status)) return false
    const checkIn = new Date(t.check_in)
    return checkIn >= now && checkIn <= endDate
  })
}

/**
 * Get recent checkouts (last N days)
 */
export function getRecentCheckouts(
  transients: TransientStayTableRow[],
  days: number = 30
): TransientStayTableRow[] {
  const now = new Date()
  const startDate = new Date(now)
  startDate.setDate(startDate.getDate() - days)

  return transients.filter((t) => {
    if (t.status !== 'checked_out') return false
    const checkOut = t.actual_check_out ? new Date(t.actual_check_out) : new Date(t.expected_check_out)
    return checkOut >= startDate && checkOut <= now
  })
}

/**
 * Group transients by check-in date
 */
export function groupByCheckInDate(
  transients: TransientStayTableRow[]
): Map<string, TransientStayTableRow[]> {
  const groups = new Map<string, TransientStayTableRow[]>()

  for (const transient of transients) {
    const dateKey = transient.check_in.split('T')[0]
    const existing = groups.get(dateKey) || []
    existing.push(transient)
    groups.set(dateKey, existing)
  }

  // Sort by date
  return new Map([...groups.entries()].sort((a, b) => a[0].localeCompare(b[0])))
}

/**
 * Format date for section header (e.g., "FEB 7 (Friday)")
 */
export function formatDateHeader(dateStr: string): string {
  const date = new Date(dateStr + 'T12:00:00') // Noon to avoid timezone issues
  const month = date.toLocaleDateString('en-US', { month: 'short' }).toUpperCase()
  const day = date.getDate()
  const weekday = date.toLocaleDateString('en-US', { weekday: 'long' })
  return `${month} ${day} (${weekday})`
}

// ============================================
// Helper Functions
// ============================================

/**
 * Format currency amount
 */
export function formatCurrency(amount: number | null): string {
  if (amount === null) return '-'
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount)
}

/**
 * Format datetime for display
 */
export function formatDateTime(dateString: string | null): string {
  if (!dateString) return '-'
  return new Date(dateString).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

/**
 * Format date only for display
 */
export function formatDate(dateString: string | null): string {
  if (!dateString) return '-'
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

/**
 * Calculate nights from check-in and check-out
 */
export function calculateNights(checkIn: string, checkOut: string): number {
  const start = new Date(checkIn)
  const end = new Date(checkOut)
  const diffTime = Math.abs(end.getTime() - start.getTime())
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
  return Math.max(1, diffDays)
}

/**
 * Calculate total amount from rate type, rate amount and nights
 */
export function calculateTotal(
  rateType: 'flat' | 'per_sqft' | 'per_night' | null,
  rateAmount: number | null,
  nights: number,
  discountAmount: number | null,
  taxAmount: number | null
): number | null {
  if (rateAmount === null || rateType === null) return null
  const subtotal = rateType === 'flat' ? rateAmount : rateAmount * nights
  const discount = discountAmount ?? 0
  const tax = taxAmount ?? 0
  return subtotal - discount + tax
}

/**
 * Check if stay is active (currently checked in)
 */
export function isCheckedIn(status: TransientStatus): boolean {
  return status === 'checked_in'
}

/**
 * Check if stay is upcoming (not yet checked in)
 */
export function isUpcoming(status: TransientStatus): boolean {
  return status === 'inquiry' || status === 'reserved' || status === 'confirmed'
}

/**
 * Check if stay is completed
 */
export function isCompleted(status: TransientStatus): boolean {
  return status === 'checked_out' || status === 'cancelled' || status === 'no_show'
}

// ============================================
// Status Transition Guards
// ============================================

/**
 * Can this transient stay be cancelled? (inquiry / reserved / confirmed)
 */
export function canCancelTransient(status: TransientStatus): boolean {
  return ['inquiry', 'reserved', 'confirmed'].includes(status)
}

/**
 * Can this transient stay be checked out? (checked_in only)
 */
export function canCheckOutTransient(status: TransientStatus): boolean {
  return status === 'checked_in'
}

/**
 * Can this transient stay be marked no-show? (reserved / confirmed)
 */
export function canNoShowTransient(status: TransientStatus): boolean {
  return ['reserved', 'confirmed'].includes(status)
}

/**
 * Can this transient stay be checked in? (reserved / confirmed)
 */
export function canCheckInTransient(status: TransientStatus): boolean {
  return ['reserved', 'confirmed'].includes(status)
}

/**
 * Can this transient stay be terminated? (checked_in only — early forced end)
 */
export function canTerminateTransient(status: TransientStatus): boolean {
  return status === 'checked_in'
}

/**
 * Get display name for transient stay
 */
export function getTransientDisplayName(
  stay: Pick<TransientStayTableRow, 'booking_number' | 'client_name' | 'tail_number'>
): string {
  const parts = [stay.booking_number]
  if (stay.tail_number) parts.push(stay.tail_number)
  if (stay.client_name) parts.push(stay.client_name)
  return parts.join(' - ')
}

// ============================================
// V2 COLUMN GROUPS (for Checked Out / All tabs)
// ============================================

export type TransientColumnGroupId = 'identity' | 'billing'

export const TRANSIENT_COLUMN_GROUPS: ColumnGroupConfig<TransientColumnGroupId>[] = [
  { id: 'identity', label: 'Identity', description: 'Booking#, Tail#, Client, Unit, Dates, Status', alwaysOn: true },
  { id: 'billing', label: 'Billing', description: 'Rate, Total, Invoice Status' },
]

// ============================================
// V2 GROUPING OPTIONS (for Checked Out / All tabs)
// ============================================

export type TransientGroupingOption = 'none' | 'status' | 'space' | 'client'

export interface TransientGroupingOptionConfig {
  value: TransientGroupingOption
  label: string
}

export const TRANSIENT_GROUPING_OPTIONS: TransientGroupingOptionConfig[] = [
  { value: 'none', label: 'None' },
  { value: 'status', label: 'By Status' },
  { value: 'space', label: 'By Space' },
  { value: 'client', label: 'By Client' },
]

// ============================================
// V2 FILTER PILL OPTIONS
// ============================================

export const TRANSIENT_STATUS_FILTER_OPTIONS = TRANSIENT_STATUSES.map((status) => ({
  value: status,
  label: TRANSIENT_STATUS_LABELS[status],
}))

// ============================================
// V2 TAB-SPECIFIC COLUMN GROUP DEFAULTS
// ============================================

export function getDefaultColumnGroups(tab: TransientTimeTab): TransientColumnGroupId[] {
  switch (tab) {
    case 'checked_out':
      return ['identity', 'billing']
    case 'all':
      return ['identity']
    default:
      return ['identity']
  }
}

export function getDefaultQuickFilters(tab: TransientTimeTab): {
  status: TransientStatus[]
  spaceType: SpaceType[]
  locationIds: string[]
} {
  switch (tab) {
    case 'checked_out':
      return { status: ['checked_out'], spaceType: [], locationIds: [] }
    case 'all':
      return { status: [], spaceType: [], locationIds: [] }
    default:
      return { status: [], spaceType: [], locationIds: [] }
  }
}
